/**
 * Customer auth against CoreMonit Platform API (PostgreSQL-backed).
 * See services/backendMode.ts for URL / proxy behavior.
 */

import {
  apiConnectionHint,
  getBackendBaseUrl,
  isBackendUserDbEnabled,
} from '../backendMode';

export { isBackendUserDbEnabled };

/** localStorage keys — keep in sync with AuthContext */
export const CUSTOMER_ACCESS_TOKEN_KEY = 'coremonit_customer_token';
export const CUSTOMER_REFRESH_TOKEN_KEY = 'coremonit_customer_refresh_token';

function parseError(res: Response, data: unknown): string {
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    if (typeof d.exception_message === 'string') return d.exception_message;
    if (typeof d.detail === 'string') return d.detail;
    if (d.detail && typeof d.detail === 'object') {
      const inner = d.detail as Record<string, unknown>;
      if (typeof inner.message === 'string') return inner.message;
    }
    if (typeof d.message === 'string') return d.message;
  }
  return `Request failed (${res.status})`;
}

async function apiFetch(url: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (e) {
    // fetch() only throws before any response (network/CORS/mixed content). Not the same as HTTP 4xx/5xx.
    if (e instanceof TypeError) {
      const browserMsg = e instanceof Error && e.message ? ` ${e.message}` : '';
      throw new Error(apiConnectionHint(getBackendBaseUrl()) + browserMsg);
    }
    throw e;
  }
}

export async function customerEmailExists(email: string): Promise<boolean> {
  const base = getBackendBaseUrl();
  const res = await apiFetch(
    `${base}/api/auth/customer/exists?email=${encodeURIComponent(email.trim())}`
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    console.error('[customerEmailExists] API error', {
      status: res.status,
      url: res.url,
      body: data,
    });
    throw new Error(parseError(res, data));
  }
  const j = (await res.json()) as { exists?: boolean };
  return j.exists === true;
}

export type CustomerSignupPayload = {
  email: string;
  password: string;
  name: string;
  first_name?: string;
  last_name?: string;
  phone_number: string;
  company: string;
  account_type: 'work' | 'personal';
};

export async function customerSignup(payload: CustomerSignupPayload): Promise<void> {
  const base = getBackendBaseUrl();
  const res = await apiFetch(`${base}/api/auth/customer/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseError(res, data));
}

export async function resendSignupOtp(email: string, phone_number: string): Promise<void> {
  const base = getBackendBaseUrl();
  const res = await apiFetch(`${base}/api/auth/customer/resend-signup-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.trim(), phone_number }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseError(res, data));
}

export type CustomerLoginResponse = {
  access_token: string;
  refresh_token: string;
  token_type: string;
  /** Access token TTL in seconds (server). */
  expires_in?: number;
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    organization_id?: string | null;
  };
};

export type CustomerOAuthStatus = {
  enabled: boolean;
  google: { configured: boolean };
  customer_app_url?: string;
};

/** Which SSO providers are available (no secrets exposed). */
export async function fetchCustomerOAuthStatus(): Promise<CustomerOAuthStatus> {
  const base = getBackendBaseUrl();
  const res = await apiFetch(`${base}/api/auth/customer/oauth/status`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { enabled: false, google: { configured: false } };
  }
  return data as CustomerOAuthStatus;
}

/** Redeem one-time code after Google redirect (same response shape as login). */
export async function exchangeOAuthLoginCode(code: string): Promise<CustomerLoginResponse> {
  const base = getBackendBaseUrl();
  const res = await apiFetch(`${base}/api/auth/customer/oauth/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code: code.trim() }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseError(res, data));
  return data as CustomerLoginResponse;
}

export async function customerLogin(email: string, password: string): Promise<CustomerLoginResponse> {
  const base = getBackendBaseUrl();
  const res = await apiFetch(`${base}/api/auth/customer/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.trim(), password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseError(res, data));
  return data as CustomerLoginResponse;
}

/** Store access + refresh tokens after login or refresh. */
export function persistCustomerSessionTokens(j: CustomerLoginResponse): void {
  if (typeof localStorage === 'undefined') return;
  const access = j.access_token;
  const refresh = j.refresh_token;
  if (!access || typeof access !== 'string' || access === 'undefined') {
    console.warn('[auth] Login/refresh response missing access_token.');
    return;
  }
  localStorage.setItem(CUSTOMER_ACCESS_TOKEN_KEY, access);
  if (!refresh || typeof refresh !== 'string' || refresh === 'undefined') {
    console.warn('[auth] Login/refresh response missing refresh_token — sign-in will work until access token expires.');
    localStorage.removeItem(CUSTOMER_REFRESH_TOKEN_KEY);
    return;
  }
  localStorage.setItem(CUSTOMER_REFRESH_TOKEN_KEY, refresh);
}

/**
 * Use a valid refresh token to obtain new access + refresh tokens.
 */
function decodeJwtExpIso(accessToken: string): string | null {
  try {
    const parts = accessToken.split('.');
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = '='.repeat((4 - (b64.length % 4)) % 4);
    const json = atob(b64 + pad);
    const payload = JSON.parse(json) as { exp?: number };
    return typeof payload.exp === 'number' ? new Date(payload.exp * 1000).toISOString() : null;
  } catch {
    return null;
  }
}

export async function customerRefreshTokens(): Promise<boolean> {
  if (typeof localStorage === 'undefined') return false;
  const refresh = localStorage.getItem(CUSTOMER_REFRESH_TOKEN_KEY);
  if (!refresh || refresh === 'undefined') return false;
  const base = getBackendBaseUrl();
  const res = await apiFetch(`${base}/api/auth/customer/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refresh }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    console.warn('[customer-session] POST /api/auth/customer/refresh failed', {
      status: res.status,
      detail: data,
    });
    return false;
  }
  const payload = data as CustomerLoginResponse;
  if (!payload.access_token || !payload.refresh_token) {
    console.warn('[customer-session] POST /api/auth/customer/refresh missing tokens in body', data);
    return false;
  }
  persistCustomerSessionTokens(payload);
  console.info('[customer-session] refresh success', {
    expires_in: payload.expires_in,
    access_expires_at: decodeJwtExpIso(payload.access_token),
    user_id: payload.user?.id,
  });
  return true;
}

/** Complete team invite from email link (?invite=TOKEN). */
export async function customerAcceptInvite(token: string, password: string): Promise<void> {
  const base = getBackendBaseUrl();
  const res = await apiFetch(`${base}/api/auth/customer/accept-invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: token.trim(), password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseError(res, data));
}
